import { mapOpenFoodFactsProduct, normalizeBarcode } from "./mapper";
import { RateGate } from "./rate-gate";
import { parseProductResponse, parseSearchResponse } from "./schemas";
import { FoodSourceError, type FoodSearchResult, type ImportedFoodDraft } from "./types";

const API_ROOT = "https://world.openfoodfacts.org";
const SEARCH_ENDPOINT = "/api/open-food-facts/search";
const FIELDS = [
  "code",
  "product_name",
  "generic_name",
  "brands",
  "serving_size",
  "serving_quantity",
  "serving_quantity_unit",
  "quantity",
  "product_quantity",
  "product_quantity_unit",
  "image_front_small_url",
  "image_front_url",
  "nutriments",
].join(",");

export interface OpenFoodFactsClientOptions {
  fetch?: typeof fetch;
  searchMinimumIntervalMs?: number;
  productMinimumIntervalMs?: number;
  cacheTtlMs?: number;
  requestTimeoutMs?: number;
}

export class OpenFoodFactsClient {
  private readonly request: typeof fetch;
  private readonly searchGate: RateGate;
  private readonly productGate: RateGate;
  private readonly cacheTtlMs: number;
  private readonly requestTimeoutMs: number;
  private readonly cache = new Map<string, { expiresAt: number; value: unknown }>();
  private readonly inFlight = new Map<string, Promise<unknown>>();

  constructor(options: OpenFoodFactsClientOptions = {}) {
    this.request = options.fetch ?? globalThis.fetch.bind(globalThis);
    // Conservative OFF limits: 10 search requests/minute and 15 product reads/minute.
    this.searchGate = new RateGate(options.searchMinimumIntervalMs ?? 6_100);
    this.productGate = new RateGate(options.productMinimumIntervalMs ?? 4_100);
    this.cacheTtlMs = options.cacheTtlMs ?? 24 * 60 * 60 * 1_000;
    this.requestTimeoutMs = options.requestTimeoutMs ?? 15_000;
  }

  async search(query: string, options: { page?: number; pageSize?: number; signal?: AbortSignal } = {}): Promise<FoodSearchResult> {
    const normalized = query.trim().replace(/\s+/g, " ");
    if (normalized.length < 2) return { items: [], page: 1 };
    const page = clampInteger(options.page ?? 1, 1, 100);
    const pageSize = clampInteger(options.pageSize ?? 20, 1, 25);
    const parameters = new URLSearchParams({
      q: normalized,
      page: String(page),
      page_size: String(pageSize),
      fields: FIELDS,
    });
    const key = `search:${parameters}`;
    const data = await this.cachedRequest(key, `${SEARCH_ENDPOINT}?${parameters}`, this.searchGate, options.signal);
    let parsed;
    try {
      parsed = parseSearchResponse(data);
    } catch {
      throw new FoodSourceError("The food database returned an unexpected response", "invalid-response");
    }
    return {
      items: parsed.hits.map(mapOpenFoodFactsProduct).filter(isPresent),
      page: parsed.page ?? page,
      pageCount: parsed.page_count ?? (parsed.count === undefined
        ? undefined
        : Math.ceil(parsed.count / (parsed.page_size && parsed.page_size > 0 ? parsed.page_size : pageSize))),
      totalCount: parsed.count,
    };
  }

  async findByBarcode(input: string, signal?: AbortSignal): Promise<ImportedFoodDraft | undefined> {
    const barcode = normalizeBarcode(input);
    if (!barcode) throw new TypeError("Barcode must contain 6 to 14 digits");
    const parameters = new URLSearchParams({ fields: FIELDS });
    const key = `product:${barcode}`;
    const data = await this.cachedRequest(
      key,
      `${API_ROOT}/api/v2/product/${encodeURIComponent(barcode)}.json?${parameters}`,
      this.productGate,
      signal,
    );
    let parsed;
    try {
      parsed = parseProductResponse(data);
    } catch {
      throw new FoodSourceError("The food database returned an unexpected response", "invalid-response");
    }
    if (parsed.status === 0 || !parsed.product) return undefined;
    return mapOpenFoodFactsProduct({ ...parsed.product, code: parsed.product.code ?? barcode });
  }

  private async cachedRequest(
    key: string,
    url: string,
    gate: RateGate,
    signal?: AbortSignal,
  ): Promise<unknown> {
    const cached = this.cache.get(key);
    if (cached && cached.expiresAt > Date.now()) return cached.value;
    const current = this.inFlight.get(key);
    if (current) return current;
    const promise = this.fetchJson(url, gate, signal).then((value) => {
      this.cache.set(key, { expiresAt: Date.now() + this.cacheTtlMs, value });
      return value;
    });
    this.inFlight.set(key, promise);
    try {
      return await promise;
    } finally {
      this.inFlight.delete(key);
    }
  }

  private async fetchJson(url: string, gate: RateGate, signal?: AbortSignal): Promise<unknown> {
    if (typeof navigator !== "undefined" && navigator.onLine === false) {
      throw new FoodSourceError("Connect to the internet to search public foods", "offline");
    }
    await gate.enter(signal);
    const timeout = new AbortController();
    const timeoutId = setTimeout(() => timeout.abort(new DOMException("Request timed out", "TimeoutError")), this.requestTimeoutMs);
    const combined = combineSignals(signal, timeout.signal);
    try {
      const response = await this.request(url, {
        headers: { Accept: "application/json" },
        signal: combined,
      });
      if (response.status === 429) {
        const retry = Number(response.headers.get("retry-after"));
        throw new FoodSourceError("The food database is busy; try again shortly", "rate-limited", Number.isFinite(retry) ? retry : undefined);
      }
      if (!response.ok) throw new FoodSourceError("The food database request failed", "remote-error");
      return await response.json() as unknown;
    } catch (error) {
      if (error instanceof FoodSourceError) throw error;
      if (combined.aborted) throw combined.reason ?? new DOMException("Aborted", "AbortError");
      throw new FoodSourceError("Could not reach the food database", "remote-error");
    } finally {
      clearTimeout(timeoutId);
    }
  }
}

function combineSignals(...signals: Array<AbortSignal | undefined>): AbortSignal {
  const available = signals.filter((signal): signal is AbortSignal => Boolean(signal));
  if (available.length === 1) return available[0];
  if (typeof AbortSignal.any === "function") return AbortSignal.any(available);
  const controller = new AbortController();
  for (const signal of available) {
    if (signal.aborted) controller.abort(signal.reason);
    else signal.addEventListener("abort", () => controller.abort(signal.reason), { once: true });
  }
  return controller.signal;
}

function clampInteger(value: number, minimum: number, maximum: number): number {
  return Math.min(maximum, Math.max(minimum, Math.round(value)));
}

function isPresent<T>(value: T | undefined): value is T {
  return value !== undefined;
}
